import { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { createContactMessage, getSiteSettings } from '@/lib/api';
import { toast } from 'sonner';
import SEO from '@/components/shared/SEO';

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSiteSettings();
        const s = res.data;
        setContactInfo([
          { icon: Mail, title: 'Email Us', content: s.email, link: `mailto:${s.email}` },
          { icon: Phone, title: 'Call Us', content: s.phone, link: `tel:${s.phone.replace(/\s/g, '')}` },
          { icon: MapPin, title: 'Visit Us', content: s.address, link: null },
          { icon: Clock, title: 'Business Hours', content: s.business_hours, link: null },
        ]);
      } catch {
        setContactInfo([
          { icon: Mail, title: 'Email Us', content: 'info@sleonline.in', link: 'mailto:info@sleonline.in' },
          { icon: Phone, title: 'Call Us', content: '+91 123 456 7890', link: 'tel:+911234567890' },
          { icon: MapPin, title: 'Visit Us', content: 'Jaipur, Rajasthan, India', link: null },
          { icon: Clock, title: 'Business Hours', content: 'Mon - Sat: 9AM - 6PM', link: null },
        ]);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await createContactMessage(formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with Shubha Lakshmi Enterprises for bulk orders, product inquiries, and partnership opportunities. Located in Jaipur, Rajasthan." />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-[#FAFAF9]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="section-overline text-[#D4AF37]">Get In Touch</span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mt-4"
            style={{ fontFamily: 'Manrope' }}
          >
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground mt-6 max-w-2xl mx-auto">
            Have questions or need a quote? We're here to help. 
            Reach out to us and our team will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#FAFAF9] p-8 md:p-10 rounded-2xl">
                <h2 className="text-2xl font-bold text-primary mb-6" style={{ fontFamily: 'Manrope' }}>
                  Send us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="bg-white"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="bg-white"
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="bg-white"
                        required
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        className="bg-white"
                        required
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements..."
                      rows={6}
                      className="bg-white resize-none"
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto rounded-full bg-primary hover:bg-primary/90 px-8 py-6"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {contactInfo.map((info, idx) => (
                  <div
                    key={idx}
                    className="bg-[#FAFAF9] p-6 rounded-xl flex items-start gap-4"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary" style={{ fontFamily: 'Manrope' }}>
                        {info.title}
                      </p>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-muted-foreground">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="mt-8 map-container h-64 md:h-80">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d227749.0494585995!2d75.62574965!3d26.885431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396c4adf4c57e281%3A0xce1c63a0cf22e09!2sJaipur%2C%20Rajasthan!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SLE Online Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#FAFAF9]" data-testid="contact-faq">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="section-title text-3xl md:text-4xl" style={{ fontFamily: 'Manrope' }}>
              Frequently Asked Questions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'What is your minimum order quantity?',
                a: 'Minimum order quantity varies by product. Contact us for specific details.',
              },
              {
                q: 'Do you offer samples?',
                a: 'Yes, we provide samples for quality evaluation before bulk orders.',
              },
              {
                q: 'What are your payment terms?',
                a: 'We offer flexible payment terms including advance, credit, and COD for regular customers.',
              },
              {
                q: 'How long does delivery take?',
                a: 'Standard delivery takes 3-7 business days. Express delivery is available.',
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl">
                <h3 className="font-semibold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>
                  {faq.q}
                </h3>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
